import { ChangeDetectionStrategy, Component, effect, inject, signal, TemplateRef, untracked, viewChild } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';

import {
  ButtonModule,
  DIALOG_DATA,
  DialogRef,
  FileModel,
  IconModule,
  isEmpty,
  OkDialog,
  SpinnerComponent,
  UploaderModule,
} from '@hglobal/fin-ui';
import { FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgProgressbar } from 'ngx-progressbar';

import { WalletCardComponent } from '@kr/components';
import { generateTraceparent } from '@kr/core';
import { environment } from '@kr/environment';
import { WalletCard } from '@kr/models';

import { MASS_PAYOUT_API, MassPayOutApiService } from './api';
import { MassPayOutDetailsDialog } from './components';
import { MassPayoutStore } from './mass-pay-out.store';
import { MassPayOut } from './models';
import { getTransactionsMock } from '../transactions/mocks';

const LIMIT_PER_CHUNK = 3;
const POSSIBLE_TIMEOUT_ERROR = 'error0';

@Component({
  imports: [
    TranslateModule,
    IconModule,
    ButtonModule,
    WalletCardComponent,
    ReactiveFormsModule,
    UploaderModule,
    NgProgressbar,
    SpinnerComponent,
  ],
  providers: [
    MassPayOutDetailsDialog,
    MassPayoutStore,
    {
      provide: MASS_PAYOUT_API,
      useClass: environment.useMocks ? MassPayOutApiService : MassPayOutApiService,
    },
  ],
  selector: 'kr-mass-pay-out',
  templateUrl: './mass-pay-out.component.html',
  styleUrls: ['./mass-pay-out.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MassPayOutComponent {
  readonly #fb = inject(FormBuilder);
  readonly #translate = inject(TranslateService);
  readonly #dialogRef = inject(DialogRef);
  readonly #okDialog = inject(OkDialog);
  readonly #store = inject(MassPayoutStore);
  readonly #massPayOutDetailsDialog = inject(MassPayOutDetailsDialog);
  readonly #data = inject<{ wallet: WalletCard }>(DIALOG_DATA);

  failedTpl = viewChild<TemplateRef<unknown>>('failedTpl');
  progressBar = viewChild<NgProgressbar>('progressBar');

  wallet: WalletCard = this.#data.wallet;
  form: FormGroup<GroupResolverFormBuilder<{ file: FileModel }>> = this.#fb.group({
    file: [<FileModel>null, Validators.required],
  });
  apiHost = environment.apiHost;
  useMocks = environment.useMocks;
  errMessage: string;

  isEstimateExecuting = signal<boolean>(false);

  readonly progressBarEffect = effect(() => {
    const progress = this.#store.progressBar();
    if (progress) {
      untracked(() => this.progressBar().set(progress));
    }
  });

  readonly estimatesAndRiskScoresEffect = effect(() => {
    const isExecuted = this.#store.isGetEstimatesAndRiskScoresSuccess();
    if (isExecuted && this.isEstimateExecuting()) {
      untracked(() => {
        this.#completeExecution();
        this.#openMassPayOutDetailsDialog(this.#store.massPayOutEstimates());
      });
    }
  });

  readonly estimatesAndRiskScoresErrorEffect = effect(() => {
    const error = this.#store.getEstimatesAndRiskScoresError();
    if (error && this.isEstimateExecuting()) {
      untracked(() => {
        this.#completeExecution();
        this.openFailedDialog(error, true);
      });
    }
  });

  get header() {
    return `${this.wallet.name}/${this.#translate.instant('massPayOut.title')}`;
  }

  next(): void {
    this.progressBar().set(0);
    this.isEstimateExecuting.set(true);

    if (this.useMocks) {
      const massPayOut = {
        massPayOut: getTransactionsMock(),
        totalFee: '0.50005',
        totalAmount: '45.000045',
        balanceAvailable: '60.000045',
        networkBalance: '0.008',
      };
      this.#openMassPayOutDetailsDialog(massPayOut);
      return;
    }

    this.#store.resetRequestStatus('getEstimatesAndRiskScores');

    const { requestConcurrencyLimitCount, chunks, chunksCount } = this.form.controls.file.value.data;
    const limitPerChunk = requestConcurrencyLimitCount || LIMIT_PER_CHUNK;

    const wallet = this.wallet;
    const providerId = this.wallet.organizationId;

    this.#store.getEstimatesAndRiskScores({
      chunks,
      chunksCount,
      limitPerChunk,
      providerId,
      wallet,
    });
  }

  openFailedDialog(error: any, isEstimationError: boolean): void {
    this.errMessage =
      error?.code === POSSIBLE_TIMEOUT_ERROR
        ? 'transferDetails.estimation.failed.message'
        : ((isEstimationError ? error?.originalError?.error?.error_description : error?.error_description) ?? 'Unknown error');
    this.#okDialog
      .open({
        headerKey: this.header,
        contentTpl: this.failedTpl(),
        noOk: true,
        noCancel: true,
      })
      .afterClosed()
      .subscribe(() => this.#dialogRef.close(false));
  }

  close(): void {
    this.#dialogRef.close();
  }

  getHeaders(): { name: string; value: string }[] {
    const orgId = this.wallet.organizationId.toString();

    return [
      { name: 'org-ids', value: orgId },
      { name: 'traceparent', value: generateTraceparent() },
    ];
  }

  #openMassPayOutDetailsDialog(massPayOut: MassPayOut): void {
    this.#massPayOutDetailsDialog
      .open(this.wallet, massPayOut)
      .afterClosed()
      .subscribe(data => !isEmpty(data) && this.#dialogRef.close(data));
  }

  #completeExecution(): void {
    this.progressBar().complete();
    this.isEstimateExecuting.set(false);
  }
}
